import React, { useCallback, useState } from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type { DictadoPayload, DictadoRuntime } from '../../core/types/payloads/dictado.types';
import type { AnatomiaPayload, AnatomiaRuntime } from '../../core/types/payloads/anatomia.types';

function dispatchSubmit(dispatch: ActivityViewProps['dispatch'], action: string, data?: unknown) {
  void dispatch({ type: action, payload: data });
}

function useRhythmPlayback(syllables: string[]) {
  const [playing, setPlaying] = useState(false);

  const play = useCallback(() => {
    if (playing || !syllables.length) return;
    setPlaying(true);
    const beatMs = 520;
    let i = 0;
    const tick = () => {
      if (i >= syllables.length) {
        setPlaying(false);
        return;
      }
      const utter = new SpeechSynthesisUtterance(syllables[i]);
      utter.rate = 1.15;
      utter.onend = () => {
        i += 1;
        window.setTimeout(tick, beatMs * 0.35);
      };
      window.speechSynthesis.speak(utter);
    };
    tick();
  }, [playing, syllables]);

  return { playing, play };
}

// ── Dictado ──
export function DictadoStudent({ payload, runtime, dispatch, participantId }: ActivityViewProps<'dictado'>) {
  const rt = runtime as DictadoRuntime;
  const value = participantId ? rt.responses[participantId] ?? '' : '';
  const finalized = participantId ? rt.finalized[participantId] : false;
  const { playing, play } = useRhythmPlayback(payload.patternSyllables);

  return (
    <div className="eval-block eval-block--dictado">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__body">{payload.studentPrompt}</p>
      <button type="button" className="sala-btn-hero mb-4 text-sm" disabled={playing} onClick={play}>
        {playing ? '▶ Reproduciendo…' : '▶ Escuchar patrón'}
      </button>
      <textarea
        className="sala-input-dark min-h-[5rem] w-full text-sm"
        disabled={finalized}
        value={value}
        placeholder="Escribe las sílabas Takadimi que escuchaste…"
        onChange={(e) => dispatchSubmit(dispatch, 'FILL', { value: e.target.value })}
      />
      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Enviar transcripción
        </button>
      )}
    </div>
  );
}

export function DictadoHost({ payload }: ActivityViewProps<'dictado'>) {
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__hint">{payload.teacherScript}</p>
      <p className="eval-block__body mt-2">
        Patrón: {payload.patternSymbols?.join(' ') ?? ''} ({payload.patternSyllables.join(' ')})
      </p>
    </div>
  );
}

export function DictadoProjector(props: ActivityViewProps<'dictado'>) {
  return <DictadoHost {...props} />;
}

// ── Anatomía ──
export function AnatomiaStudent({ payload, runtime, dispatch, participantId }: ActivityViewProps<'anatomia'>) {
  const rt = runtime as AnatomiaRuntime;
  const answers = participantId ? rt.responses[participantId] ?? {} : {};
  const finalized = participantId ? rt.finalized[participantId] : false;

  return (
    <div className="eval-block eval-block--anatomia">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__hint">Compás {payload.meter ?? '4/4'} — indica cuántos tiempos vale cada figura.</p>
      <ul className="eval-anatomia-list">
        {payload.items.map((item) => (
          <li key={item.id} className="eval-anatomia-row">
            <span className="eval-anatomia-symbol" aria-hidden>
              {item.symbol}
            </span>
            <span className="eval-anatomia-label">{item.figureLabel}</span>
            <input
              className="eval-anatomia-input sala-input-dark text-sm"
              type="text"
              inputMode="decimal"
              disabled={finalized}
              value={answers[item.id] ?? ''}
              placeholder="tiempos"
              onChange={(e) =>
                dispatchSubmit(dispatch, 'FILL', { itemId: item.id, value: e.target.value })
              }
            />
          </li>
        ))}
      </ul>
      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Enviar respuestas
        </button>
      )}
    </div>
  );
}

export function AnatomiaHost({ payload, runtime }: ActivityViewProps<'anatomia'>) {
  const rt = runtime as AnatomiaRuntime;
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__hint">
        Enviados: {rt.aggregates.submitted} · Respuestas correctas (ítems): {rt.aggregates.correct}
      </p>
    </div>
  );
}

export function AnatomiaProjector(props: ActivityViewProps<'anatomia'>) {
  return <AnatomiaHost {...props} />;
}
